import React, { useRef, useState, useEffect } from "react";
import jsQR from "jsqr";

const QRCodeScanner = ({ onScan }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [qrCode, setQrCode] = useState(null);

    useEffect(() => {
        let stream;

        const startCamera = async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "environment" },
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.setAttribute("playsinline", true);
                    videoRef.current.onloadedmetadata = () => {
                        videoRef.current.play().catch(console.error);
                    };
                }
            } catch (err) {
                console.error("Error accessing camera:", err);
            }
        };

        const stopCamera = () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                }
                stream = null;
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopCamera();
            } else {
                startCamera();
            }
        };

        const handleUnload = () => {
            stopCamera();
        };

        startCamera();

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("beforeunload", handleUnload);

        return () => {
            stopCamera();
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, []);

    useEffect(() => {
        let animationFrameId;

        const scan = () => {
            if (!videoRef.current || !canvasRef.current) return;

            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");

            if (video.videoWidth === 0 || video.videoHeight === 0) {
                animationFrameId = requestAnimationFrame(scan);
                return;
            }

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const qr = jsQR(imageData.data, imageData.width, imageData.height);

            context.lineWidth = 6;

            if (qr) {
                const raw = qr.data;
                setQrCode(raw);

                context.beginPath();
                context.strokeStyle = "lime";

                const loc = qr.location;
                context.moveTo(loc.topLeftCorner.x, loc.topLeftCorner.y);
                context.lineTo(loc.topRightCorner.x, loc.topRightCorner.y);
                context.lineTo(loc.bottomRightCorner.x, loc.bottomRightCorner.y);
                context.lineTo(loc.bottomLeftCorner.x, loc.bottomLeftCorner.y);
                context.lineTo(loc.topLeftCorner.x, loc.topLeftCorner.y);

                context.stroke();

                if (raw.startsWith("P")) {
                    const barcode = raw.split(";")[0]; // only the vigenereCode
                    onScan?.(barcode);
                }
            }

            animationFrameId = requestAnimationFrame(scan);
        };

        animationFrameId = requestAnimationFrame(scan);
        return () => cancelAnimationFrame(animationFrameId);
    }, [onScan]);

    return (
        <div style={{ position: "relative", width: "100%", maxWidth: 600 }}>
            <video
                ref={videoRef}
                style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                }}
            />
            <canvas
                ref={canvasRef}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 2,
                    borderRadius: "10px",
                }}
            />

            {qrCode && (
                <div
                    style={{
                        position: "absolute",
                        top: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "5px",
                        fontSize: "14px",
                    }}
                >
                    QR Code: {qrCode}
                </div>
            )}
        </div>
    );
};

export default QRCodeScanner;
